// src/hooks/useDocumentMeta.js
import { useEffect, useMemo } from "react";

function upsertMetaByName(name, content) {
  if (!name) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content ?? "");
  return el;
}

function upsertMetaByProperty(property, content) {
  if (!property) return null;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content ?? "");
  return el;
}

function upsertLinkCanonical(href) {
  if (!href) return null;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
  return link;
}

/**
 * useDocumentMeta({
 *   title,
 *   description,
 *   canonical,
 *   robots,
 *   og: { title, description, url, image, type },
 *   twitter: { card, site, creator, title, description, image }
 * })
 */
export default function useDocumentMeta(cfg = {}) {
  const ogKey = useMemo(() => JSON.stringify(cfg.og || {}), [cfg.og]);
  const twKey = useMemo(() => JSON.stringify(cfg.twitter || {}), [cfg.twitter]);

  useEffect(() => {
    const prevTitle = document.title;
    const disposers = [];

    // Title
    if (cfg.title) {
      document.title = cfg.title;
    }

    // Description
    if (cfg.description) {
      const el = upsertMetaByName("description", cfg.description);
      disposers.push(() => el?.parentNode?.removeChild(el));
    }

    // Robots
    if (cfg.robots) {
      const el = upsertMetaByName("robots", cfg.robots);
      disposers.push(() => el?.parentNode?.removeChild(el));
    }

    // Canonical
    if (cfg.canonical) {
      const link = upsertLinkCanonical(cfg.canonical);
      disposers.push(() => link?.parentNode?.removeChild(link));
    }

    // Open Graph
    if (cfg.og) {
      const { title, description, url, image, type } = cfg.og;
      const els = [];
      if (title) els.push(upsertMetaByProperty("og:title", title));
      if (description) els.push(upsertMetaByProperty("og:description", description));
      if (url) els.push(upsertMetaByProperty("og:url", url));
      if (image) els.push(upsertMetaByProperty("og:image", image));
      if (type) els.push(upsertMetaByProperty("og:type", type));
      disposers.push(() => els.forEach((n) => n?.parentNode?.removeChild(n)));
    }

    // Twitter
    if (cfg.twitter) {
      const { card, site, creator, title, description, image } = cfg.twitter;
      const els = [];
      if (card) els.push(upsertMetaByName("twitter:card", card));
      if (site) els.push(upsertMetaByName("twitter:site", site));
      if (creator) els.push(upsertMetaByName("twitter:creator", creator));
      if (title) els.push(upsertMetaByName("twitter:title", title));
      if (description) els.push(upsertMetaByName("twitter:description", description));
      if (image) els.push(upsertMetaByName("twitter:image", image));
      disposers.push(() => els.forEach((n) => n?.parentNode?.removeChild(n)));
    }

    return () => {
      document.title = prevTitle;
      disposers.forEach((dispose) => dispose());
    };
  // ✅ Incluimos cfg.og y cfg.twitter para satisfacer ESLint
  }, [cfg.title, cfg.description, cfg.canonical, cfg.robots, cfg.og, cfg.twitter, ogKey, twKey]);
}
